import React, { useState } from 'react';
import { rowFor } from '../utils/rtl';
import { Platform, ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import * as Clipboard from 'expo-clipboard';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { useLocale } from '../contexts/LocaleContext';
import type { WorkspaceAction } from '../services/workspace-assistant.types';
import { colors, spacing, typography } from '../theme';
import { workspaceTextBlocks, workspaceTextSpans } from '../utils/workspaceText';
import { exportFileName, linkSegments, toCsv, toTsv } from '../utils/workspaceExport';
import { saveTextFile } from '../utils/saveTextFile';

export type WorkspaceTextLink = { label: string; action: WorkspaceAction };

/**
 * An assistant reply: headings, paragraphs, bullet / numbered lists and tables (text only — no HTML
 * or URLs). Names of records the reply's lookups returned can be tapped to open them; tables can be
 * copied or downloaded.
 */
export function WorkspaceAssistantText({ content, isRTL, links = [], onOpen }: { content: string; isRTL: boolean; links?: WorkspaceTextLink[]; onOpen?: (action: WorkspaceAction) => void }) {
  const styles = useThemedStyles(getStyles);
  const { t } = useLocale();
  const [copied, setCopied] = useState<number | null>(null);
  const dir = { textAlign: isRTL ? 'right' as const : 'left' as const, writingDirection: isRTL ? 'rtl' as const : 'ltr' as const };
  const row = { flexDirection: rowFor(isRTL) };
  const labels = links.map(l => l.label);
  const spans = (text: string) => workspaceTextSpans(text).flatMap((span, i) => span.code || !onOpen || !labels.length
    ? [<Text key={i} style={[span.emphasis && styles.strong, span.code && styles.code]}>{span.text}</Text>]
    : linkSegments(span.text, labels).map((part, j) => part.link === undefined
      ? <Text key={`${i}-${j}`} style={span.emphasis && styles.strong}>{part.text}</Text>
      : <Text key={`${i}-${j}`} accessibilityRole="link" suppressHighlighting onPress={() => onOpen(links[part.link!].action)} style={[styles.link, span.emphasis && styles.strong]}>{part.text}</Text>));
  const plain = (text: string) => workspaceTextSpans(text).map(s => s.text).join('');
  return <View style={styles.body}>
    {workspaceTextBlocks(content).map((block, index) => {
      switch (block.type) {
        case 'blank': return <View key={index} style={styles.break} />;
        case 'heading': return <Text key={index} selectable style={[styles.heading, dir]}>{spans(block.text)}</Text>;
        case 'paragraph': return <Text key={index} selectable style={[styles.line, dir]}>{spans(block.text)}</Text>;
        case 'bullets':
        case 'numbers': return <View key={index} style={styles.list}>
          {block.items.map((item, i) => <View key={i} style={[styles.item, row]}>
            <Text style={[styles.marker, block.type === 'numbers' && styles.number]}>{block.type === 'numbers' ? `${block.start + i}.` : '•'}</Text>
            <Text selectable style={[styles.line, styles.itemText, dir]}>{spans(item)}</Text>
          </View>)}
        </View>;
        case 'table': {
          const columns = Math.max(block.header.length, ...block.rows.map(r => r.length));
          const grid = { header: block.header.map(plain), rows: block.rows.map(r => Array.from({ length: columns }, (_, c) => plain(r[c] || ''))) };
          const cell = (text: string | undefined, header: boolean, key: number) =>
            <View key={key} style={[styles.cell, header && styles.headCell]}><Text selectable style={[styles.cellText, header && styles.strong, dir]}>{spans(text || '')}</Text></View>;
          return <View key={index} style={styles.tableBlock}>
            <ScrollView horizontal showsHorizontalScrollIndicator style={styles.tableScroll} contentContainerStyle={{ flexDirection: rowFor(isRTL) }}>
              <View style={styles.table}>
                <View style={[styles.tableRow, row]}>{Array.from({ length: columns }, (_, c) => cell(block.header[c], true, c))}</View>
                {block.rows.map((r, ri) => <View key={ri} style={[styles.tableRow, row, ri % 2 === 1 && styles.stripe]}>{Array.from({ length: columns }, (_, c) => cell(r[c], false, c))}</View>)}
              </View>
            </ScrollView>
            <View style={[styles.tools, row]}>
              <TouchableOpacity accessibilityRole="button" accessibilityLabel={t('workspaceAssistant.table.copy')} {...tip(t('workspaceAssistant.table.copy'))} style={styles.tool}
                onPress={async () => { try { await Clipboard.setStringAsync(toTsv(grid)); setCopied(index); setTimeout(() => setCopied(c => (c === index ? null : c)), 1500); } catch { /* clipboard unavailable */ } }}>
                <Ionicons name={copied === index ? 'checkmark' : 'copy-outline'} size={13} color={colors.text.secondary as string} />
              </TouchableOpacity>
              <TouchableOpacity accessibilityRole="button" accessibilityLabel={t('workspaceAssistant.table.csv')} {...tip(t('workspaceAssistant.table.csv'))} style={styles.tool}
                onPress={() => void saveTextFile(toCsv(grid), exportFileName(grid.header[0] || 'table', 'csv'), 'text/csv')}>
                <Ionicons name="download-outline" size={14} color={colors.text.secondary as string} />
              </TouchableOpacity>
            </View>
          </View>;
        }
      }
    })}
  </View>;
}

const tip = (title: string) => (Platform.OS === 'web' ? { title } as any : {});

const getStyles = () => StyleSheet.create({
  body: { gap: 4 },
  line: { color: colors.text.primary, fontSize: 14, lineHeight: 23 },
  heading: { color: colors.text.primary, fontSize: 15, lineHeight: 24, fontFamily: typography.fontFamily.semibold, fontWeight: '600', marginTop: 2 },
  strong: { fontFamily: typography.fontFamily.semibold, fontWeight: '600' },
  code: { fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace', fontSize: 13, backgroundColor: colors.background.secondary },
  link: { color: colors.primary, textDecorationLine: 'underline', textDecorationColor: String(colors.primary) + '66' },
  break: { height: 6 },
  list: { gap: 3 },
  item: { gap: spacing.xs, alignItems: 'flex-start' },
  marker: { color: colors.primary, fontSize: 14, lineHeight: 23, minWidth: 12, textAlign: 'center' },
  number: { minWidth: 20, fontFamily: typography.fontFamily.semibold },
  itemText: { flex: 1 },
  tableBlock: { marginVertical: 4, gap: 2 },
  tableScroll: {},
  table: { borderWidth: 1, borderColor: colors.border, borderRadius: 8, overflow: 'hidden' },
  tableRow: { borderBottomWidth: 1, borderBottomColor: colors.border },
  stripe: { backgroundColor: colors.background.secondary },
  cell: { minWidth: 88, maxWidth: 240, paddingHorizontal: 10, paddingVertical: 7, justifyContent: 'center' },
  headCell: { backgroundColor: colors.primaryTransparent },
  cellText: { color: colors.text.primary, fontSize: 13, lineHeight: 19 },
  tools: { gap: 2 },
  tool: { width: 26, height: 26, borderRadius: 13, alignItems: 'center', justifyContent: 'center' },
});
